import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { api } from '@/api/client';

/** Password change for the signed-in user (user menu) — POST /auth/change-password. */
export function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [oldPassword, setOld] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const close = () => { setOld(''); setNew(''); setConfirm(''); setShow(false); onClose(); };

  async function submit() {
    if (newPassword.length < 8) { toast.push('New password must be at least 8 characters.', 'error'); return; }
    if (newPassword !== confirm) { toast.push('New password and confirmation do not match.', 'error'); return; }
    if (newPassword === oldPassword) { toast.push('New password must be different from the current one.', 'error'); return; }
    setBusy(true);
    try {
      await api.post('/auth/change-password', { oldPassword, newPassword });
      toast.push('Password changed successfully.', 'success');
      close();
    } catch (e) {
      toast.push(apiError(e, 'Could not change password — check your details.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  const type = show ? 'text' : 'password';
  return (
    <Modal
      open={open}
      onClose={close}
      size="sm"
      title="Change Password"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="glass" onClick={close} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={busy || !oldPassword || !newPassword || !confirm}>
            {busy ? 'Saving…' : 'Change Password'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Field label="Current password" value={oldPassword} onChange={setOld} type={type} autoComplete="current-password" />
        <Field label="New password (min 8 chars)" value={newPassword} onChange={setNew} type={type} autoComplete="new-password" />
        <Field label="Confirm new password" value={confirm} onChange={setConfirm} type={type} autoComplete="new-password" />
        <label className="flex cursor-pointer items-center gap-2 text-xs text-[var(--text-muted)]">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Show passwords
        </label>
      </div>
    </Modal>
  );
}

export function Field({ label, value, onChange, type, autoComplete }: {
  label: string; value: string; onChange: (v: string) => void; type: string; autoComplete: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete} />
    </div>
  );
}
